import { deptAction, facilityAction, roleAction } from '@api'
import { genderOptions } from '@data'
import { useSpecialty, useStaff } from '@hook'
import { yupResolver } from '@hookform/resolvers/yup'
import { addStaffSchema } from '@schema'
import { AddStaffFormData } from '@type/staff.types'
import { DateInputField, FormButton, FormInputField, Loader, Modal, SelectInputField } from '@ui'
import { FormSelect, Select } from '@ui/SearchInput/Select'
import { getError, showToast } from '@util'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'

type Props = {
  onClose: () => void
  id?: string
}

export const AddStaff = ({ id, onClose }: Props) => {
  const { singleStaff, createStaff, editStaff, singleStaffIsLoading, singleIncluded } = useStaff({ id })
  const methods = useForm<AddStaffFormData>({
    resolver: yupResolver(addStaffSchema)
  })
  const [departmentIdByName, setDepartmentIdByName] = useState<Record<string, string>>({})
  const [departmentOptions, setDepartmentOptions] = useState<{ label: string; value: string; departmentId: string }[]>(
    []
  )
  const [facilityOptions, setFacilityOptions] = useState<{ label: string; value: string; facilityId: string }[]>([])
  const [defaultFacilityOption, setDefaultFacilityOption] = useState<{
    facilityId: string
    label: string
    value: string
  } | null>(null)
  const [defaultFacilityId, setDefaultFacilityId] = useState('')
  const [defaultDepartmentId, setDefaultDepartmentId] = useState('')
  const [defaultSpecialtyId, setDefaultSpecialtyId] = useState('')
  const previousDepartmentRef = useRef<string>('')
  const departmentVal = methods.watch('department')
  const roleVal = methods.watch('role')
  const selectedDepartmentId = departmentVal ? departmentIdByName[departmentVal] || defaultDepartmentId || '' : ''
  const { data: specialtyData, createSpecialty } = useSpecialty({
    department_id: selectedDepartmentId || undefined,
    sort_by: 'name',
    sort_dir: 'asc'
  })

  const {
    handleSubmit,
    reset,
    setValue,
    formState: { isSubmitting }
  } = methods

  const specialtyOptions = useMemo(
    () =>
      (specialtyData || []).map(item => ({
        label: item.attributes.name,
        value: item.attributes.name,
        specialtyId: item.id
      })),
    [specialtyData]
  )

  useEffect(() => {
    if (!id) {
      setDefaultFacilityOption(null)
      setFacilityOptions([])
      return
    }
    if (!singleStaff) return

    const departmentId = singleStaff.relationships.department.data?.id || ''
    const specialtyId = singleStaff.relationships.specialty.data?.id || ''
    const facilityId = singleStaff.relationships.facility?.data?.id || ''
    const departmentName =
      singleIncluded?.find(({ id: includedId, type }) => includedId === departmentId && type === 'department')
        ?.attributes.name || departmentId
    const specialtyName =
      singleIncluded?.find(({ id: includedId, type }) => includedId === specialtyId && type === 'specialty')?.attributes
        .name || specialtyId
    const facilityName =
      singleIncluded?.find(({ id: includedId, type }) => includedId === facilityId && type === 'facility')?.attributes
        .name || facilityId
    if (departmentName && departmentId) {
      setDepartmentIdByName(prev => ({ ...prev, [departmentName]: departmentId }))
    }

    setDefaultDepartmentId(departmentId)
    setDefaultSpecialtyId(specialtyId)
    const facilityOption = { label: facilityName, value: facilityId, facilityId }
    setDefaultFacilityId(facilityId)
    setDefaultFacilityOption(facilityOption)
    setFacilityOptions([facilityOption])
    reset({
      ...singleStaff.attributes,
      email: singleIncluded?.find(({ type }) => type === 'user')?.attributes.email,
      department: departmentName,
      middle_name: singleStaff.attributes.middle_name || '',
      specialty: specialtyName,
      role: singleStaff?.meta.roles[0],
      facility: facilityId
    })
  }, [id, reset, singleIncluded, singleStaff])

  useEffect(() => {
    if (!departmentVal) {
      previousDepartmentRef.current = ''
      return
    }

    if (!previousDepartmentRef.current) {
      previousDepartmentRef.current = departmentVal
      return
    }

    if (previousDepartmentRef.current !== departmentVal) {
      setValue('specialty', '')
      previousDepartmentRef.current = departmentVal
    }
  }, [departmentVal, setValue])

  const loadDepartmentOptions = async (query?: string) => {
    const departments = await deptAction(query)
    const options = (departments || []).map(({ label, value }) => ({
      label,
      value: label,
      departmentId: value
    }))

    setDepartmentIdByName(prev => {
      const next = { ...prev }
      options.forEach(option => {
        next[option.value] = option.departmentId
      })
      return next
    })

    setDepartmentOptions(options)
    return options
  }

  const loadFacilityOptions = async (query?: string) => {
    const facilities = await facilityAction(query)
    const options = (facilities || []).map(({ label, value }) => ({
      label,
      value: value,
      facilityId: value
    }))

    return options
  }

  const onCreateSpecialty = async (name: string) => {
    const departmentId = selectedDepartmentId

    if (!departmentId) {
      showToast('Please select a department first')
      return
    }

    const payload = {
      data: {
        type: 'specialty',
        attributes: {
          name
        },
        relationships: {
          department: {
            data: {
              id: departmentId,
              type: 'department'
            }
          }
        }
      }
    }

    try {
      const response = await createSpecialty(payload)
      const createdId = response?.data?.id || response?.id
      if (createdId) {
        setDefaultSpecialtyId(String(createdId))
      }
      setValue('specialty', name, { shouldDirty: true, shouldValidate: true })
      showToast('Specialty created successfully')
    } catch (error) {
      getError(error)
    }
  }

  const onSubmit = async ({ email, role, specialty, department, facility, ...rest }: AddStaffFormData) => {
    const departmentId = departmentIdByName[department] || defaultDepartmentId || department
    const specialtyId =
      specialtyOptions.find(option => option.value === specialty)?.specialtyId || defaultSpecialtyId || specialty
    const facilityId =
      facilityOptions.find(option => option.value === facility)?.facilityId || defaultFacilityId || facility
    const formInputVal = {
      data: {
        attributes: {
          email,
          ...rest
        },
        relationships: {
          department: {
            data: {
              id: departmentId,
              type: 'department'
            }
          },
          role: {
            data: {
              id: role,
              type: 'role'
            }
          },
          specialty: {
            data: {
              id: specialtyId,
              type: 'specialty'
            }
          },
          user: {
            data: {
              id: email,
              type: 'Staff'
            }
          },
          facility: {
            data: {
              id: facilityId,
              type: 'facility'
            }
          }
        },
        type: 'staff'
      }
    }
    try {
      id ? await editStaff({ formVal: formInputVal, id }) : await createStaff(formInputVal)
      showToast(id ? 'Detail updated successfully' : `${rest.first_name} has been created successfully`)
      onClose()
    } catch (error) {
      getError(error)
    }
  }

  return (
    <FormProvider {...methods}>
      <Modal onClose={onClose} title={id ? 'Edit Staff' : 'Add Staff'}>
        {singleStaffIsLoading && <Loader />}
        <form className="flex flex-col overflow-auto" onSubmit={handleSubmit(onSubmit)}>
          <div
            className="overflow-auto scrollbar-color-#fff scrollbar-width-thin p-1 lg:w-[550px] flex flex-col"
            style={{ display: 'block', scrollbarColor: '#fff', scrollbarWidth: 'thin' }}
          >
            <div className="flex flex-col gap-5 p-6">
              <FormInputField label="First Name" name="first_name" placeholder="" />
              <FormInputField label="Last Name" name="last_name" placeholder="" />
              <FormInputField label="Other Name" name="middle_name" placeholder="" />
              <FormInputField disabled={!!id} label="Email" name="email" placeholder="" />
              <FormInputField label="Phone Number" name="phone_number" placeholder="" />
              <SelectInputField
                label="Gender"
                labelKey={'name'}
                name="gender"
                options={genderOptions}
                placeholder="Select"
                valueKey={{ key: 'value', reduce: true }}
              />
              <DateInputField label="Date Of Birth" name="date_of_birth" />
              <Controller
                control={methods.control}
                name="facility"
                render={({ field: { onChange, value }, fieldState: { error } }) => {
                  const selectedOption =
                    facilityOptions.find(option => option.value === value) ||
                    (defaultFacilityOption?.value === value ? defaultFacilityOption : null)

                  return (
                    <Select
                      borderRadius={'100px'}
                      cacheOptions
                      defaultOptions={facilityOptions.length > 0 ? facilityOptions : true}
                      error={error?.message}
                      getOptionLabel={({ label }: any) => label}
                      getOptionValue={({ value }: any) => value}
                      isAsync
                      label="Facility"
                      loadOptions={loadFacilityOptions}
                      onChange={(newValue: any) => {
                        onChange(newValue?.value || '')
                        if (newValue?.value) {
                          setFacilityOptions([
                            {
                              label: newValue.label,
                              value: newValue.value,
                              facilityId: newValue.value
                            }
                          ])
                        }
                      }}
                      placeholder="E.g Hospital"
                      value={selectedOption}
                    />
                  )
                }}
              />
              <Controller
                control={methods.control}
                name="department"
                render={({ field: { onChange, value }, fieldState: { error } }) => {
                  const selectedOption =
                    departmentOptions.find(option => option.value === value) || (value ? { label: value, value } : null)

                  return (
                    <Select
                      borderRadius={'100px'}
                      createOptionPosition="first"
                      defaultOptions
                      error={error?.message}
                      getOptionLabel={({ label }: any) => label}
                      getOptionValue={({ value }: any) => value}
                      isAsync
                      label="Department"
                      loadOptions={loadDepartmentOptions}
                      onChange={(newValue: any) => onChange(newValue?.value || '')}
                      placeholder="E.g surgery"
                      value={selectedOption}
                    />
                  )
                }}
              />
              <Controller
                control={methods.control}
                name="specialty"
                render={({ field: { onChange, value }, fieldState: { error } }) => {
                  const selectedOption =
                    specialtyOptions.find(option => option.value === value) || (value ? { label: value, value } : null)

                  const handleChange = (newValue: any) => {
                    if (!newValue) {
                      onChange('')
                      return
                    }

                    if (typeof newValue === 'string') {
                      onChange(newValue)
                    } else if (newValue?.value) {
                      onChange(newValue.value)
                    }
                  }

                  return (
                    <Select
                      borderRadius={'100px'}
                      createOptionPosition="first"
                      error={error?.message}
                      getOptionLabel={({ label }: any) => label}
                      getOptionValue={({ value }: any) => value}
                      isCreatable
                      isDisabled={!selectedDepartmentId}
                      label="Specialty"
                      onChange={handleChange}
                      onCreateOption={onCreateSpecialty}
                      options={specialtyOptions}
                      placeholder="E.g cardiology"
                      value={selectedOption}
                    />
                  )
                }}
              />
              <FormSelect
                defaultOptions
                getOptionLabel={({ name }) => name}
                getOptionValue={({ value }) => value}
                isAsync
                label="Role"
                loadOptions={roleAction}
                name="role"
                value={{ name: roleVal, value: roleVal }}
              />
            </div>
          </div>
          <FormButton isLoading={isSubmitting} onClose={onClose} submit={id ? 'Update' : 'Add Staff'} />
        </form>
      </Modal>
    </FormProvider>
  )
}
